import { useEffect, useRef } from 'react';
import { Backdrop, Box, CircularProgress, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { useRoleSwitchProgress } from '@/auth/roleSwitchProgress';

/**
 * Above the sidebar and header (1200-1201), menus, dialogs and the role
 * selection screen (2000); below the role-changed dialog (3000), the only way
 * on for a tab blocked by a switch elsewhere.
 */
export const ROLE_SWITCH_OVERLAY_Z_INDEX = 2500;

/**
 * Full-screen cover while this tab switches role (auth/roleSwitchProgress.ts):
 * from the click in the role menu, or the page load adopting another role,
 * until the page reloads. Nothing of the previous role's page can be clicked
 * or seen refetching meanwhile.
 *
 * Not a modal: focus moves onto the status text, so a screen reader reads it,
 * but nothing is trapped or hidden from assistive technology. When the switch
 * fails, focus goes back where it was.
 */
export const RoleSwitchOverlay = () => {
  const progress = useRoleSwitchProgress();
  const statusRef = useRef<HTMLDivElement | null>(null);
  const open = progress !== null;

  useEffect(() => {
    if (!open) return undefined;
    const previous = document.activeElement as HTMLElement | null;
    // after the role menu returned focus to its anchor
    const frame = window.setTimeout(() => statusRef.current?.focus(), 0);
    return () => {
      window.clearTimeout(frame);
      if (previous?.isConnected) previous.focus?.();
    };
  }, [open]);

  if (!progress) return null;

  return (
    <Backdrop
      open
      data-testid='role-switch-overlay'
      // a backdrop is aria-hidden by default; this one carries the status
      slotProps={{ root: { 'aria-hidden': undefined } }}
      sx={(theme) => ({
        zIndex: ROLE_SWITCH_OVERLAY_Z_INDEX,
        color: theme.palette.text.primary,
        backgroundColor: alpha(theme.palette.background.default, 0.92),
        flexDirection: 'column',
      })}
    >
      <Box
        ref={statusRef}
        tabIndex={-1}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          outline: 'none',
        }}
      >
        <CircularProgress color='primary' aria-hidden='true' />
        <Typography role='status' aria-live='polite' variant='body1' sx={{ fontWeight: 500 }}>
          {progress.message}
        </Typography>
      </Box>
    </Backdrop>
  );
};

export default RoleSwitchOverlay;
